import React, { useState } from 'react';
import { useDateTimeContext } from '@/shared/context/datetime/useDateTimeContext';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import {
  Calendar,
  X,
  RotateCcw,
  Check,
  XCircle,
  Clock,
  Settings,
} from 'lucide-react';
import { isDevelopment, isTesting } from '@/shared/config/config';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';

/**
 * Panel de Testing de DateTime
 *
 * Panel flotante que permite controlar la fecha de prueba del sistema
 * Solo visible en development/staging
 *
 * Características:
 * - Ver fecha actual (real o de prueba)
 * - Establecer fecha de prueba personalizada
 * - Resetear a fecha del sistema
 * - Indicador visual de modo de testing activo
 *
 * SEGURIDAD:
 * - Bloqueado automáticamente en producción
 * - Solo funcional en development/staging
 */
const DateTimeTestingPanelContent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [testDateInput, setTestDateInput] = useState('');
  const { showError } = useGlobalNotifications();

  const {
    now,
    format,
    isTestMode,
    setTestDate,
    resetTestDate,
    testDate,
    getTimezone,
  } = useDateTimeContext();

  const currentDate = now();
  const timezone = getTimezone();
  const isInTestMode = isTestMode();

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const handleSetTestDate = () => {
    if (!testDateInput) {
      return;
    }

    try {
      const date = new Date(testDateInput);
      if (isNaN(date.getTime())) {
        showError('Fecha inválida', 'Fecha inválida. Usa formato ISO 8601: YYYY-MM-DDTHH:mm:ss');
        return;
      }
      setTestDate(date);
      setTestDateInput('');
    } catch (error) {
      showError('Error al parsear fecha', 'Error al parsear la fecha. Usa formato ISO 8601.');
    }
  };

  const handleReset = () => {
    resetTestDate();
    setTestDateInput('');
  };

  const handleQuickSetDate = (daysOffset: number) => {
    const date = new Date();
    date.setDate(date.getDate() + daysOffset);
    setTestDate(date);
  };

  const StatusIcon: React.FC<{ status: boolean }> = ({ status }) => {
    return status ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-gray-400" />
    );
  };

  // Botón flotante cuando está cerrado
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={togglePanel}
          size="sm"
          variant="outline"
          className={`rounded-full shadow-lg flex items-center justify-center ${
            isInTestMode
              ? 'bg-yellow-100 hover:bg-yellow-200 border-yellow-400'
              : 'bg-white hover:bg-gray-50'
          }`}
          title="DateTime Testing Panel"
        >
          <Calendar
            className={`h-4 w-4 flex-shrink-0 ${
              isInTestMode ? 'text-yellow-700' : 'text-gray-700'
            }`}
          />
          {isInTestMode && (
            <span className="ml-2 text-xs font-semibold text-yellow-700">
              TEST MODE
            </span>
          )}
        </Button>
      </div>
    );
  }

  // Panel abierto
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96">
      <Card className="shadow-xl border-2 bg-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <CardTitle className="text-lg">DateTime Testing</CardTitle>
            </div>
            <Button
              onClick={togglePanel}
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Test Mode Badge */}
          {isInTestMode && (
            <div className="mt-2">
              <Badge className="bg-yellow-100 text-yellow-800 w-full justify-center">
                ⚠️ MODO DE PRUEBA ACTIVO
              </Badge>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4 text-sm">
          {/* Current Date/Time */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Fecha Actual
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span>Fecha:</span>
                <span className="font-mono text-xs">
                  {format(currentDate, 'PP')}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Hora:</span>
                <span className="font-mono text-xs">
                  {format(currentDate, 'pp')}
                </span>
              </div>
              <div className="flex justify-between">
                <span>ISO:</span>
                <span className="font-mono text-xs break-all">
                  {currentDate.toISOString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Timezone:</span>
                <span className="text-xs">{timezone}</span>
              </div>
            </div>
          </div>

          {/* Test Mode Status */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
              <Settings className="h-4 w-4 mr-1" />
              Estado del Sistema
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span>Test Mode:</span>
                <div className="flex items-center">
                  <StatusIcon status={isInTestMode} />
                  <span
                    className={`ml-2 text-xs ${
                      isInTestMode ? 'text-yellow-700 font-semibold' : 'text-gray-600'
                    }`}
                  >
                    {isInTestMode ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span>Fecha de Prueba:</span>
                <span className="text-xs">
                  {testDate ? format(testDate, 'PP') : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Usando Fecha:</span>
                <span className="text-xs font-semibold">
                  {isInTestMode ? 'Prueba' : 'Sistema'}
                </span>
              </div>
            </div>
          </div>

          {/* Set Test Date */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">
              Establecer Fecha de Prueba
            </h4>
            <div className="space-y-2">
              {/* Quick Date Buttons */}
              <div className="grid grid-cols-3 gap-1">
                <Button
                  onClick={() => handleQuickSetDate(0)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Hoy
                </Button>
                <Button
                  onClick={() => handleQuickSetDate(15)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  +15 días
                </Button>
                <Button
                  onClick={() => handleQuickSetDate(30)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  +30 días
                </Button>
              </div>

              {/* Custom Date Input */}
              <div className="space-y-1">
                <label className="text-xs text-gray-600">
                  Fecha personalizada (ISO 8601):
                </label>
                <input
                  type="text"
                  value={testDateInput}
                  onChange={(e) => setTestDateInput(e.target.value)}
                  placeholder="2024-06-15T00:00:00.000Z"
                  className="w-full px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Set Button */}
              <Button
                onClick={handleSetTestDate}
                disabled={!testDateInput}
                variant="primary"
                size="sm"
                className="w-full"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Establecer Fecha
              </Button>

              {/* Helper Text */}
              <p className="text-xs text-gray-500">
                Ejemplos:
                <br />• 2024-06-15T00:00:00.000Z
                <br />• 2024-12-31T23:59:59.000Z
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t">
            <div className="flex space-x-2">
              <Button
                onClick={handleReset}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Resetear
              </Button>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
            ℹ️ Los cambios afectan toda la aplicación. Los cron jobs están
            deshabilitados en modo de prueba.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * DateTimeTestingPanel - Wrapper con validación de entorno
 *
 * Solo se renderiza en development/staging
 * Bloqueado automáticamente en producción
 */
const DateTimeTestingPanel: React.FC = () => {
  // SEGURIDAD: Solo mostrar en development/staging
  if (!isDevelopment() && !isTesting()) {
    return null;
  }

  return <DateTimeTestingPanelContent />;
};

export default DateTimeTestingPanel;
